import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon } from 'lucide-react';
import { Badge } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import {
  useUnreadBroadcastCount,
  useVendorBroadcasts,
} from '@/entities/vendor-broadcast';
import { FreshBroadcastsDialog } from './fresh-broadcasts-dialog';
import { NotificationsDrawer } from './notifications-drawer';

/**
 * The header notification center — the old `TopBar` bell, ported. A round
 * utility button (the `AppSwitcher` twin) with the unread count, opening the
 * history drawer; fresh items additionally auto-open the dialog once per set.
 * Mounted in each shell's utilities — only one shell mounts at a time, so the
 * auto-popup effect never double-fires.
 */
export function NotificationsCenter({ className }: { className?: string }) {
  const { t } = useTranslation('common');
  const { data: unreadCount = 0 } = useUnreadBroadcastCount();
  const { data: freshBroadcasts = [], isLoading: isFreshLoading } =
    useVendorBroadcasts('fresh');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const shownIdsRef = useRef('');

  // The auto-popup is keyed off the sorted fresh-id list, so a poll returning
  // the same set doesn't reopen the dialog after the merchant dismissed it —
  // only a NEW set pops again.
  useEffect(() => {
    if (isFreshLoading || dialogOpen) return;
    const ids = freshBroadcasts
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .join(',');
    if (ids && shownIdsRef.current !== ids) {
      shownIdsRef.current = ids;
      setDialogOpen(true);
    }
  }, [dialogOpen, freshBroadcasts, isFreshLoading]);

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        aria-label={t('broadcasts.notifications')}
        className={cn(
          'relative flex size-14 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-foreground',
          'hover:bg-secondary-hover transition-colors outline-none',
          'focus-visible:ring-[3px] focus-visible:ring-ring/40',
          className,
        )}
      >
        <BellIcon className="size-6" />
        {unreadCount > 0 && (
          <Badge variant="destructive" shape="count" className="absolute top-1 right-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>
      <NotificationsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <FreshBroadcastsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
